/**
 * Rejoining a deaf-to-deaf group call after reloading its room.
 *
 * Leaving a group call (hangup, closing the tab, reloading the room page)
 * records the member as left on the server, and the room refuses them until
 * they accept the call again: p2p_accept { callId, roomName, callerId }, which
 * the server answers with p2p_join_room, or an error such as CALL_FULL. The
 * room pill (vrs-room-controls.js) sends call_end on pagehide and, in a group
 * room, leaves this tab a marker so the reloaded page can ask to come back in
 * before it joins the conference. The marker is tab-scoped (sessionStorage):
 * closing the tab is leaving, and nothing should bring that back.
 */
/** sessionStorage, web only: { callId, roomName, callerId, leftAt }. */
export const GROUP_REJOIN_KEY = 'vrs_group_rejoin';
/** A reload is seconds; anything older is not a reload. */
export const GROUP_REJOIN_MAX_AGE_MS = 10 * 60 * 1000;
/** How long the room waits for the server to seat it again. */
export const GROUP_REJOIN_TIMEOUT_MS = 10000;
// Answers to p2p_accept that mean "not coming back in". They carry no call id,
// so any of them while a rejoin is pending is taken as its answer.
const REJOIN_REFUSALS = ['CALL_ENDED', 'CALL_FULL', 'FORBIDDEN', 'INTERNAL_ERROR', 'INVITE_UNAVAILABLE', 'NOT_FOUND'];
let pendingRejoinCallId;
/**
 * Reads a marker left for this room, if it is recent and well formed.
 *
 * @param {string|null} raw - The stored value.
 * @param {string} roomName - The room this page is joining.
 * @param {number} now - The current time.
 * @returns {IGroupRejoin|null}
 */
export function parseGroupRejoin(raw, roomName, now = Date.now()) {
    try {
        const value = JSON.parse(raw || 'null');
        if (!value || typeof value !== 'object'
            || typeof value.callId !== 'string' || !value.callId
            || typeof value.callerId !== 'string' || !value.callerId
            || value.roomName !== roomName || !roomName.startsWith('group-')
            || typeof value.leftAt !== 'number' || now - value.leftAt > GROUP_REJOIN_MAX_AGE_MS || value.leftAt > now) {
            return null;
        }
        return { callId: value.callId, callerId: value.callerId, leftAt: value.leftAt, roomName };
    }
    catch {
        return null;
    }
}
function sessionStore() {
    try {
        // Web only; native has no tab to reload.
        return globalThis.sessionStorage;
    }
    catch {
        return undefined;
    }
}
/**
 * The marker for this room, if this tab just left it.
 *
 * @param {string} roomName - The room this page is joining.
 * @returns {IGroupRejoin|null}
 */
export function findGroupRejoin(roomName) {
    const store = sessionStore();
    return store ? parseGroupRejoin(store.getItem(GROUP_REJOIN_KEY), roomName) : null;
}
/**
 * Forget the marker once the rejoin is settled either way (a timeout keeps
 * it, so reloading again retries).
 *
 * @returns {void}
 */
export function clearGroupRejoin() {
    sessionStore()?.removeItem(GROUP_REJOIN_KEY);
}
/**
 * Whether this page is waiting to be seated again in that call: its
 * p2p_join_room is the answer, not an invitation to navigate somewhere.
 *
 * @param {string} [callId] - The call in question; any pending rejoin if omitted.
 * @returns {boolean}
 */
export function isGroupRejoinPending(callId) {
    return Boolean(pendingRejoinCallId) && (callId === undefined || callId === pendingRejoinCallId);
}
/**
 * Ask to be seated again and wait for the answer.
 *
 * @param {IRejoinService} service - The queue service.
 * @param {IGroupRejoin} rejoin - The call to rejoin.
 * @param {number} timeoutMs - How long to wait.
 * @returns {Promise<GroupRejoinOutcome>}
 */
export function requestGroupRejoin(service, rejoin, timeoutMs = GROUP_REJOIN_TIMEOUT_MS) {
    return new Promise(resolve => {
        let settled = false;
        const finish = (outcome) => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            service.off('p2pJoinRoom', onJoin);
            service.off('error', onError);
            pendingRejoinCallId = undefined;
            if (outcome.ok || outcome.code !== 'TIMEOUT') {
                clearGroupRejoin();
            }
            resolve(outcome);
        };
        const onJoin = (data) => {
            if (data?.callId === rejoin.callId) {
                finish({ ok: true });
            }
        };
        const onError = (data) => {
            if (!data?.operation && REJOIN_REFUSALS.includes(String(data?.code || ''))) {
                finish({ code: String(data.code), ok: false });
            }
        };
        const timer = setTimeout(() => finish({ code: 'TIMEOUT', ok: false }), timeoutMs);
        pendingRejoinCallId = rejoin.callId;
        service.on('p2pJoinRoom', onJoin);
        service.on('error', onError);
        try {
            service.acceptP2PCall({ callId: rejoin.callId, callerId: rejoin.callerId, roomName: rejoin.roomName });
        }
        catch {
            finish({ code: 'TIMEOUT', ok: false });
        }
    });
}
/**
 * Why Join or Rejoin on Home (web client-profile.js startRejoin, native VRS
 * Home) did not open the call, in the person's terms. Unlike a reloaded
 * room, Home has nothing to reload: it asks to try again.
 *
 * @param {string} code - The refusal code, or TIMEOUT.
 * @returns {string}
 */
export function groupJoinFailureMessage(code) {
    switch (code) {
        case 'CALL_FULL':
            return 'This call is full — 8 people max. Try again when someone leaves.';
        case 'CALL_ENDED':
        case 'NOT_FOUND':
            return 'This call has ended.';
        case 'TIMEOUT':
            return 'Could not join the call. Try again.';
        default:
            return 'You can no longer join this call.';
    }
}
/**
 * Why the room did not open again, in the person's terms.
 *
 * @param {string} code - The refusal code.
 * @returns {string}
 */
export function groupRejoinFailureMessage(code) {
    switch (code) {
        case 'CALL_FULL':
            return 'This call is full — 8 people max. Try again when someone leaves.';
        case 'CALL_ENDED':
        case 'NOT_FOUND':
            return 'This call has ended.';
        case 'TIMEOUT':
            return 'We could not reconnect you to the call. Reload to try again.';
        default:
            return 'You can no longer rejoin this call.';
    }
}
